// catalogue/supabase.ts — CatalogueProvider backed by a Supabase-loaded snapshot.
//
// The engine stays synchronous: Nexus loads referentials up front and passes a snapshot here.
// Items and combinations come from the snapshot; PA, workshops and formats delegate to bound engine data when absent.

import type { QuoteLine } from "../core/core";
import type { Combination } from "../../models/data.model";
import {
  paSupplierLookup,
  type PaSupplierOptions,
  type PaMode,
  type PaSupplierResult,
  type PaRateCards,
} from "../pricing/pa-margins";
import { resolveSstFamily, lineWorkshopMenu } from "../workshops/workshops";
import { fuzzySearch } from "../search/fuzzySearch";
import { MAT_FORMATS, DEFAULT_MAT_FORMAT } from "../../data";
import type { CatalogueProvider, CatalogueItem, TierItem, WorkshopItem, SearchResult } from "./provider";

/** Snapshot shape loaded from Supabase (items, combinations, optional itemTiers and paGrids). */
export interface CatalogueSnapshot {
  items?: readonly CatalogueItem[];
  itemTiers?: Readonly<Record<string, TierItem[]>>;
  combinations?: readonly Combination[];
  /** Purchase PA grids (workshop × key, 6 tiers). Omitted values fall back to bound engine data. */
  paGrids?: PaRateCards;
}

const NON_IMPL = "SupabaseProvider — snapshot catalogue requis (items / combinations).";
/**
 * Accent- and case-insensitive form of a string, for search.
 * @param s The string to normalise.
 * @returns The normalised string.
 */
const norm = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/** CatalogueProvider backed by a snapshot loaded from Supabase. */
export class SupabaseProvider implements CatalogueProvider {
  private readonly index: Map<string, CatalogueItem>;

  /**
   * @param snap The catalogue snapshot (items, combinations, item tiers, PA grids).
   */
  constructor(private readonly snap: CatalogueSnapshot = {}) {
    this.index = new Map((snap.items ?? []).map((i) => [i.ref, i]));
  }

  /**
   * Normalized item by ref, or undefined.
   * @param ref The item reference.
   * @returns The item, or undefined.
   */
  getItem(ref: string): CatalogueItem | undefined {
    if (!this.snap.items) throw new Error(NON_IMPL);
    return this.index.get(ref);
  }

  /**
   * Item-specific PV tiers, or [] when none.
   * @param ref The item reference.
   * @returns The item's PV tiers.
   */
  getItemTiers(ref: string): TierItem[] {
    return this.snap.itemTiers?.[ref] ?? [];
  }

  /**
   * All combinations of the snapshot.
   * @returns The combinations.
   */
  getCombinations(): readonly Combination[] {
    if (!this.snap.combinations) throw new Error(NON_IMPL);
    return this.snap.combinations;
  }

  /**
   * Combination by ref, or undefined.
   * @param ref The combination reference.
   * @returns The combination, or undefined.
   */
  getCombination(ref: string): Combination | undefined {
    return this.getCombinations().find((c) => c.ref === ref);
  }

  /**
   * Real supplier PA of a line for a given mode, or null.
   * @param l The quote line.
   * @param mode The PA mode.
   * @param opts The volume context.
   * @returns The supplier PA, or null.
   */
  getPaGrids(
    l: QuoteLine,
    mode: PaMode,
    opts?: Omit<PaSupplierOptions, 'paGrids'>,
  ): PaSupplierResult | null {
    return paSupplierLookup(l, mode, {
      ...opts,
      ...(this.snap.paGrids ? { paGrids: this.snap.paGrids } : {}),
    });
  }

  /**
   * Workshop resolution of a line (family, primary, menu).
   * @param l The quote line.
   * @returns The workshop resolution.
   */
  getItemWorkshops(l: QuoteLine): WorkshopItem {
    const family = resolveSstFamily(l);
    const menu = lineWorkshopMenu(l); // matrix union PA grids
    return { family, primary: menu.rank1, menu: menu.options };
  }

  /**
   * Formats of a material, flagging the default one.
   * @param ref The material reference.
   * @returns The formats.
   */
  getFormats(ref: string): { format: string; isDefault: boolean }[] {
    const formats = MAT_FORMATS[ref] ?? [];
    const defaultFormat = DEFAULT_MAT_FORMAT[ref];
    return formats.map((f) => ({ format: f, isDefault: f === defaultFormat }));
  }

  // Search & menus: inactive items are excluded from menus but remain priced for existing quotes.
  /**
   * True when the material or any referenced component is withdrawn from sale.
   * @param c The combination.
   * @returns Whether the combination is hidden from menus.
   */
  private combinationHiddenFromMenus(c: Combination): boolean {
    const refs = [c.material, c.print, c.cut, c.lamination, c.finishing1, c.finishing2].filter(Boolean);
    return refs.some((ref) => this.index.get(ref)?.active === false);
  }

  /**
   * Composed label for a combination (material plus components).
   * @param c The combination.
   * @returns The short label, the full name, the category and the searchable terms.
   */
  private combiLabel(c: { ref: string; material: string; print: string; cut: string; lamination: string; finishing1: string; finishing2: string; name?: string }): { itemShort: string; fullName: string; category: string; terms: string[] } {
    const it = this.index.get(c.material);
    /**
     * Short label of a component, falling back to its ref.
     * @param ref The component reference.
     * @returns The short label.
     */
    const short = (ref: string) => this.index.get(ref)?.itemShort ?? ref;
    const components = [c.print, c.cut, c.lamination, c.finishing1, c.finishing2].filter(Boolean).map(short);
    const matShort = it?.itemShort ?? c.material;
    const compose = [...components, matShort].join(" · ");
    const ownName = c.name?.trim();
    return {
      itemShort: ownName || compose,
      fullName: [it?.fullName ?? c.material, ...components].join(" · "),
      category: it?.category ?? "",
      // Composition and proper name are both searchable.
      terms: [c.ref, c.material, matShort, it?.category ?? "", c.print, c.cut, ...components, compose, ownName ?? ""],
    };
  }

  /**
   * Global search over ref, short name, full name and category; sorted and capped.
   * @param query The search text.
   * @param limit The maximum number of results.
   * @returns The matching items and combinations.
   */
  search(query: string, limit = 50): SearchResult[] {
    if (!this.snap.items || !this.snap.combinations) throw new Error(NON_IMPL);
    if (!norm(query).split(/\s+/).filter(Boolean).length) return [];
    // Fuzzy search over combi ref + full description, or item ref + names + category.
    /**
     * Sort rank of a result type: combinations first.
     * @param t The result type.
     * @returns The sort rank.
     */
    const typeOrder = (t: string) => (t === "combination" ? 0 : 1);
    const entries: { text: string; res: SearchResult }[] = [];
    for (const c of this.snap.combinations) {
      if (this.combinationHiddenFromMenus(c)) continue;
      const L = this.combiLabel(c);
      entries.push({ text: [c.ref, L.fullName, ...L.terms].join(" "), res: { ref: c.ref, type: "combination", itemShort: L.itemShort, fullName: L.fullName, category: L.category } });
    }
    for (const it of this.index.values()) {
      if (it.active === false) continue;
      entries.push({ text: [it.ref, it.fullName, it.itemShort, it.category].join(" "), res: { ref: it.ref, type: it.type, itemShort: it.itemShort, fullName: it.fullName, category: it.category } });
    }
    entries.sort((a, b) => typeOrder(a.res.type) - typeOrder(b.res.type) || a.res.category.localeCompare(b.res.category) || a.res.ref.localeCompare(b.res.ref));
    return fuzzySearch(query, entries).slice(0, limit).map((e) => e.res);
  }

  /**
   * All items of one or more types, sorted, for the dropdown mode of the hybrid input.
   * @param types The item types to list.
   * @param limit The maximum number of results.
   * @returns The items of those types.
   */
  allOfTypes(types: readonly string[], limit = 300): SearchResult[] {
    if (!this.snap.items || !this.snap.combinations) throw new Error(NON_IMPL);
    const out: SearchResult[] = [];
    for (const it of this.index.values())
      if (it.active !== false && types.includes(it.type)) out.push({ ref: it.ref, type: it.type, itemShort: it.itemShort, fullName: it.fullName, category: it.category });
    if (types.includes("combination"))
      for (const c of this.snap.combinations) {
        if (this.combinationHiddenFromMenus(c)) continue;
        const L = this.combiLabel(c);
        out.push({ ref: c.ref, type: "combination", itemShort: L.itemShort, fullName: L.fullName, category: L.category });
      }
    /**
     * Sort rank of a result type: combinations first.
     * @param t The result type.
     * @returns The sort rank.
     */
    const typeOrder = (t: string) => (t === "combination" ? 0 : 1);
    out.sort((a, b) => typeOrder(a.type) - typeOrder(b.type) || a.category.localeCompare(b.category) || a.ref.localeCompare(b.ref));
    return out.slice(0, limit);
  }

  /**
   * Returns the carried snapshot.
   * @returns The snapshot.
   */
  snapshot(): CatalogueSnapshot {
    return this.snap;
  }
}
